// D138 (§8.1) — the objective panel: a persistent top-right card with the lesson's title, the current
// instruction, the sub-goal checklist with live counters, and the attempt count (the D128 promise that
// never shipped).
//
// `ui/` consumes ViewState-derived values only (T-ARCH-004): this takes a LessonView — a plain
// view-model built by `game/lessonRunner.ts` — and never touches MatchState. textContent only, never
// innerHTML: a lesson id or a praise line is data, and data is never markup (§15.1).

import { el } from './dom';

export interface LessonHudView {
  readonly title: string;
  readonly icon: string;
  readonly attempts: number;
  readonly complete: boolean;
  readonly steps: readonly { readonly id: string; readonly instruction: string; readonly done: boolean; readonly active: boolean; readonly count: { readonly have: number; readonly need: number } | null }[];
  readonly bonus: { readonly instruction: string; readonly done: boolean } | null;
}

export class LessonHud {
  private readonly root: HTMLElement;
  private readonly head: HTMLElement;
  private readonly list: HTMLElement;
  private readonly foot: HTMLElement;

  constructor(doc: Document = document) {
    this.root = el('div', 'position:fixed;top:12px;right:12px;z-index:40;min-width:260px;max-width:340px;padding:10px 12px;' +
        'background:rgba(10,14,20,.82);color:#eef3f8;border-radius:10px;font:13px/1.45 system-ui,sans-serif;' +
        'box-shadow:0 6px 24px rgba(0,0,0,.35);pointer-events:none'
    );
    this.head = el('div', 'font-weight:700;margin-bottom:6px;letter-spacing:.2px' );
    this.list = el('div');
    this.foot = el('div', 'margin-top:7px;opacity:.6;font-size:11px' );
    this.root.append(this.head, this.list, this.foot);
    doc.body.appendChild(this.root);
  }

  update(v: LessonHudView): void {
    this.head.textContent = `${v.icon} ${v.title}`;
    this.list.replaceChildren();
    for (const s of v.steps) {
      const row = el('div', `display:flex;gap:7px;align-items:baseline;margin:3px 0;${s.done ? 'opacity:.5' : s.active ? 'opacity:1' : 'opacity:.68'}`,
      );
      const mark = el('span', `width:1.1em;flex:none;color:${s.done ? '#5cff9d' : s.active ? '#ffd166' : '#8fa0b3'}` );
      mark.textContent = s.done ? '✓' : s.active ? '▸' : '·';
      const text = el('span', s.active ? 'font-weight:600' : '' );
      text.textContent = s.count && !s.done ? `${s.instruction}  ${s.count.have}/${s.count.need}` : s.instruction;
      row.append(mark, text);
      this.list.append(row);
    }
    if (v.bonus) {
      const row = el('div', `display:flex;gap:7px;align-items:baseline;margin:6px 0 0;opacity:${v.bonus.done ? '.75' : '.5'}` );
      const mark = el('span', `width:1.1em;flex:none;color:${v.bonus.done ? '#ffd166' : '#8fa0b3'}` );
      mark.textContent = v.bonus.done ? '★' : '☆';
      const text = el('span');
      text.textContent = v.bonus.instruction;
      row.append(mark, text);
      this.list.append(row);
    }
    // attempts are FREE — the count is information, never a score, so it stays quiet at the bottom
    this.foot.textContent = v.complete ? 'Lesson complete' : `Attempt ${v.attempts}`;
  }

  destroy(): void {
    this.root.remove();
  }
}
